import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, X, Trash2, Edit3, Sparkles, Wand2, Package, Search } from "lucide-react";
import { toast } from "sonner";

const empty = {
  title: "",
  description: "",
  code: "",
  discount_type: "percent",
  discount_value: 10,
  coupon_type: "promo_code",
  category_id: "",
  starts_at: "",
  expires_at: "",
  url: "",
  image_url: "",
  featured: false,
  flash_sale: false,
  provinces: [],
  cities: [],
  product_ids: [],
};

export default function MerchantCoupons() {
  const nav = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [store, setStore] = useState(null);
  const [productQuery, setProductQuery] = useState("");

  const load = () => {
    api.get("/merchant/coupons").then((r) => setCoupons(r.data));
    api.get("/merchant/store").then((r) => setStore(r.data));
  };
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/v1/merchant/products").then((r) => setProducts(r.data.items || []));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, category_id: cats[0]?.id || "" });
    setProductQuery("");
    setModal(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c, product_ids: c.product_ids || [] });
    setProductQuery("");
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!store) return toast.error("Store profile missing");
    const isProductScoped = (form.product_ids || []).length > 0;
    // Server allows omitting category_id when product-scoped — do the same here.
    const payload = {
      ...form,
      store_id: store.id,
      discount_value: Number(form.discount_value),
      product_ids: form.product_ids || [],
    };
    if (isProductScoped && !form.category_id) delete payload.category_id;
    try {
      if (editing) {
        await api.put(`/merchant/coupons/${editing.id}`, payload);
        toast.success("Coupon updated");
      } else {
        await api.post("/merchant/coupons", payload);
        toast.success("Coupon submitted for approval");
      }
      setModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete coupon?")) return;
    await api.delete(`/merchant/coupons/${id}`);
    load();
  };

  const filteredProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return products.slice(0, 8);
    return products.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.sku || "").toLowerCase().includes(q)
    ).slice(0, 20);
  }, [products, productQuery]);

  const toggleProduct = (pid) => {
    setForm((f) => {
      const has = (f.product_ids || []).includes(pid);
      return {
        ...f,
        product_ids: has
          ? f.product_ids.filter((x) => x !== pid)
          : [...(f.product_ids || []), pid],
      };
    });
  };

  const productLabel = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return pid.slice(0, 8);
    const base = p.variant_title ? `${p.name} · ${p.variant_title}` : p.name;
    return p.sku ? `${base} · ${p.sku}` : base;
  };

  const isProductScoped = (form.product_ids || []).length > 0;

  return (
    <div data-testid="merchant-coupons">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">Coupons</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage the offers you show on Cyannect.</p>
        </div>
        <button onClick={() => setChooserOpen(true)} className="pf-btn-primary px-4 py-2 rounded-md font-semibold text-sm inline-flex items-center gap-2" data-testid="new-coupon-btn">
          <Plus className="w-4 h-4" /> New coupon
        </button>
      </div>

      {chooserOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setChooserOpen(false)} data-testid="new-coupon-chooser">
          <div className="glass-strong rounded-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl font-semibold text-slate-900">How would you like to create your coupon?</h3>
              <button onClick={() => setChooserOpen(false)} data-testid="chooser-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <button onClick={() => { setChooserOpen(false); openNew(); }}
                      className="text-left rounded-xl bg-white/85 border border-slate-200 p-5 hover:border-cyan-400 hover:shadow-md transition"
                      data-testid="chooser-manual">
                <Edit3 className="w-6 h-6 text-slate-700 mb-3" />
                <div className="font-semibold text-slate-900">Create Manually</div>
                <p className="text-sm text-slate-600 mt-1">Fill out the step-by-step form. Full control over every field.</p>
                <div className="mt-4 pf-chip pf-chip-lav text-[10px]">Classic</div>
              </button>
              <button onClick={() => { setChooserOpen(false); nav("/merchant/coupons/ai"); }}
                      className="text-left rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 border-2 border-cyan-400 p-5 hover:shadow-lg transition"
                      data-testid="chooser-ai">
                <Sparkles className="w-6 h-6 text-cyan-700 mb-3" />
                <div className="font-semibold text-slate-900 flex items-center gap-1">
                  Create with AI Assistant <Wand2 className="w-4 h-4 text-cyan-600" />
                </div>
                <p className="text-sm text-slate-600 mt-1">Describe your promotion in plain English. AI prepares the coupon for you.</p>
                <div className="mt-4 pf-chip pf-chip-mint text-[10px]">New</div>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pf-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Discount</th>
              <th className="px-5 py-3">Scope</th>
              <th className="px-5 py-3">Code</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Clicks</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`coupon-row-${c.id}`}>
                <td className="px-5 py-3 font-medium">{c.title}</td>
                <td className="px-5 py-3">{c.discount_type === "fixed" ? `CA$${c.discount_value}` : `${c.discount_value}${c.discount_type === "percent" ? "%" : ""}`}</td>
                <td className="px-5 py-3">
                  {(c.product_ids || []).length > 0 ? (
                    <span className="pf-chip pf-chip-mint inline-flex items-center gap-1" data-testid={`coupon-scope-${c.id}`}>
                      <Package className="w-3 h-3" /> {c.product_ids.length} product{c.product_ids.length === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="pf-chip pf-chip-lav" data-testid={`coupon-scope-${c.id}`}>Category</span>
                  )}
                </td>
                <td className="px-5 py-3 font-mono">{c.code || "—"}</td>
                <td className="px-5 py-3">
                  <span className={`pf-chip ${c.status === "approved" ? "bg-[color:var(--pf-mint)] text-black" : c.status === "rejected" ? "bg-destructive text-white" : "bg-secondary"}`}>{c.status}</span>
                </td>
                <td className="px-5 py-3">{c.clicks || 0}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-secondary" data-testid={`edit-coupon-${c.id}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => remove(c.id)} className="p-1.5 rounded hover:bg-secondary text-destructive" data-testid={`delete-coupon-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {coupons.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-muted-foreground">No coupons yet. Create your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-background border border-border rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" data-testid="coupon-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-2xl font-semibold">{editing ? "Edit coupon" : "New coupon"}</h3>
              <button type="button" onClick={() => setModal(false)}><X className="w-5 h-5" /></button>
            </div>

            <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-3">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="cf-title" />

            <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-3">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="cf-desc" />

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">Code (optional)</label>
                <input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background font-mono" data-testid="cf-code" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Category {isProductScoped && <span className="text-cyan-600 normal-case tracking-normal">· optional (product-scoped)</span>}
                </label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="cf-cat">
                  <option value="">{isProductScoped ? "Auto (from products)" : "Select category"}</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">Discount type</label>
                <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="cf-dtype">
                  <option value="percent">Percent</option>
                  <option value="fixed">Fixed amount</option>
                  <option value="shipping">Free shipping</option>
                  <option value="bogo">BOGO</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">Value</label>
                <input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="cf-value" />
              </div>
            </div>

            <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-3">Destination URL</label>
            <input value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="cf-url" />

            {/* Product-level scoping */}
            <div className="mt-4 rounded-xl border border-border p-3 bg-cyan-50/30" data-testid="cf-products">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-cyan-700" />
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Products (optional — leaves category-scoped if empty)
                </label>
              </div>

              {(form.product_ids || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="cf-selected-products">
                  {form.product_ids.map((pid) => (
                    <span key={pid}
                          className="pf-chip pf-chip-mint inline-flex items-center gap-1 text-[11px]"
                          data-testid={`cf-selected-${pid}`}>
                      {productLabel(pid)}
                      <button type="button" onClick={() => toggleProduct(pid)}
                              className="hover:text-rose-600"
                              data-testid={`cf-remove-${pid}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-2 relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input value={productQuery} onChange={(e) => setProductQuery(e.target.value)}
                       placeholder={products.length ? "Search your products by name or SKU…" : "No products in your catalogue yet"}
                       disabled={!products.length}
                       className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-border bg-background disabled:opacity-60"
                       data-testid="cf-product-search" />
              </div>

              {products.length > 0 && (productQuery || filteredProducts.length > 0) && (
                <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-border bg-white/70">
                  {filteredProducts.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">No matches for "{productQuery}".</div>
                  )}
                  {filteredProducts.map((p) => {
                    const picked = (form.product_ids || []).includes(p.id);
                    return (
                      <button type="button" key={p.id}
                              onClick={() => toggleProduct(p.id)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 flex items-center justify-between ${picked ? "bg-cyan-50/70" : ""}`}
                              data-testid={`cf-product-option-${p.id}`}>
                        <div>
                          <div className="text-slate-900">
                            {p.name}
                            {p.variant_title && (
                              <span className="text-slate-500 text-[11px] ml-1">· {p.variant_title}</span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {p.sku ? `SKU ${p.sku}` : "no SKU"}
                            {p.price_minor ? ` · CA$${(p.price_minor / 100).toFixed(2)}` : ""}
                          </div>
                        </div>
                        {picked && <span className="pf-chip pf-chip-mint text-[10px]">Picked</span>}
                      </button>
                    );
                  })}
                </div>
              )}
              {!products.length && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Add products under <b>Merchant → Products</b> to enable product-level coupons.
                </p>
              )}
            </div>

            <div className="flex gap-4 mt-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="cf-featured" />
                Featured
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.flash_sale} onChange={(e) => setForm({ ...form, flash_sale: e.target.checked })} data-testid="cf-flash" />
                Flash sale
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button type="submit" className="pf-btn-primary px-4 py-2 rounded-md font-semibold" data-testid="cf-submit">{editing ? "Update" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
